import React, { useCallback, useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import {
  USER_COOKIE,
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  ROLE_COOKIE,
  UserTypes,
  IMPERSONATED_SESSION_COOKIE,
  LOGGING_OUT,
} from "@src/constants/constants";
import StorageUtils from "@src/utils/storageUtils";
import SignolUtils from "@src/utils/signolUtils";
import { roleRoutes } from "../routes/routes";
import { queryClient as QueryClientSingleton } from "../utils/queryClient";
import { unstable_batchedUpdates } from "react-dom";
import { hotjarIdentifyUser } from "../utils/userActivityTracker";

/**
 * @type {React.Context<{
 *   user: undefined | {id: string, username: string, customerCode: string, permissions: any[], roles: string[], metadata: Record<string, any>, createdAt: string, firstname: string, lastname: string},
 *   isAuthenticated: boolean,
 *   activeRole: string,
 *   activeRoleDisplayName: string,
 *   activeRoleRoutePrefix: string,
 *   avatarInitials: string,
 *   impersonatedSessionContext: any,
 *   hasAdminRole: boolean,
 *   hasManagerRole: boolean,
 *   hasOperatorRole: boolean,
 *   isActiveRoleAdmin: boolean,
 *   isActiveRoleManager: boolean,
 *   isActiveRoleOperator: boolean,
 *   hasRole: (role: string) => boolean,
 *   hasRoleSwitcher: boolean,
 *   selectUserProfile: (chosenProfile) => void,
 *   setUserTokens: (tokens) => void,
 *   setImpersonatedSession: (data) => void,
 *   impersonatedSessionLogout: (resetTokens) => void,
 *   logout: () => void
 * }>}
 */
const AuthContext = React.createContext();
AuthContext.displayName = "AuthContext";

const useAuthContext = () => React.useContext(AuthContext);

const AuthProvider = ({ children, currentUser }) => {
  const queryClient = useQueryClient(QueryClientSingleton);
  const [user, setUser] = useState(currentUser);
  const [avatarInitials, setAvatarInitials] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(!!currentUser);

  const memoizedUser = useMemo(() => user, [user]);
  const roles = useMemo(() => memoizedUser?.roles || [], [memoizedUser]);

  /** Does the user have the given role / usertype */
  const hasRole = useCallback(
    (userType) => {
      return (
        !!userType &&
        isAuthenticated &&
        roles.find((r) =>
          [r.toUpperCase(), UserTypes[r].toUpperCase()].includes(
            userType.toUpperCase(),
          ),
        )
      );
    },
    [roles, isAuthenticated],
  );

  /* Active Role */
  const principalRole = useMemo(() => {
    if (roles.length === 1) return roles[0];
    const roleFromCookie = StorageUtils.getItem(ROLE_COOKIE);
    if (hasRole(roleFromCookie)) return roleFromCookie;
    return undefined;
  }, [hasRole, roles]);
  const [activeRole, setActiveRole] = useState(principalRole);
  const [activeRoleDisplayName, setActiveRoleDisplayName] = useState(
    UserTypes[activeRole],
  );
  const activeRoleRoutePrefix = useMemo(() => {
    const role = activeRole?.toUpperCase();
    const routePrefix = roleRoutes[role]?.path ?? "";
    return routePrefix;
  }, [activeRole]);

  const [impersonatedSessionContext, setImpersonatedSessionContext] = useState(
    StorageUtils.getObjectItem(IMPERSONATED_SESSION_COOKIE),
  );

  const hasAdminRole = useMemo(() => {
    return (
      hasRole("ADMINISTRATOR") &&
      (memoizedUser?.isAdmin || memoizedUser?.isSuperAdmin)
    );
  }, [hasRole, memoizedUser]);

  const hasManagerRole = useMemo(() => {
    return hasRole("FUEL_MANAGER");
  }, [hasRole]);

  const hasOperatorRole = useMemo(() => {
    return hasRole("CAPTAIN");
  }, [hasRole]);

  const isActiveRoleAdmin = useMemo(() => {
    return hasRole("ADMINISTRATOR") && activeRole === "ADMINISTRATOR";
  }, [hasRole, activeRole]);

  const isActiveRoleManager = useMemo(() => {
    return hasRole("FUEL_MANAGER") && activeRole === "FUEL_MANAGER";
  }, [hasRole, activeRole]);

  const isActiveRoleOperator = useMemo(() => {
    return hasRole("CAPTAIN") && activeRole === "CAPTAIN";
  }, [hasRole, activeRole]);

  const hasRoleSwitcher = useCallback(() => {
    return isAuthenticated && roles.length > 1;
  }, [isAuthenticated, roles]);

  const selectUserProfile = useCallback(
    (chosenProfile) => {
      if (!chosenProfile) {
        StorageUtils.removeItem(ROLE_COOKIE);
      } else {
        StorageUtils.setItem(ROLE_COOKIE, chosenProfile);
      }
      setActiveRole(chosenProfile);
      setActiveRoleDisplayName(UserTypes[chosenProfile]);
    },
    [setActiveRole, setActiveRoleDisplayName],
  );

  const setUserTokens = useCallback(
    (tokens) => {
      StorageUtils.setItem(ACCESS_TOKEN_COOKIE, tokens.accessToken);
      StorageUtils.setItem(REFRESH_TOKEN_COOKIE, tokens.refreshToken);
      if (tokens.username) {
        const mockedUsername = tokens.username;
        StorageUtils.setObjectItem(USER_COOKIE, { username: mockedUsername });
      }
      setIsAuthenticated(!!tokens.accessToken && !!tokens.refreshToken);
      //queryClient.invalidateQueries({ queryKey: ['IDENTITY_ME'] });
      queryClient.clear();
    },
    [setIsAuthenticated, queryClient],
  );

  const setImpersonatedSession = useCallback(
    (data) => {
      const session = {
        admin: { id: memoizedUser.id, username: memoizedUser.username },
        user: { ...data },
      };
      StorageUtils.setObjectItem(IMPERSONATED_SESSION_COOKIE, session);
      StorageUtils.removeItem(USER_COOKIE);
      StorageUtils.removeItem(ROLE_COOKIE);
      StorageUtils.removeItem(ACCESS_TOKEN_COOKIE);
      unstable_batchedUpdates(() => {
        setImpersonatedSessionContext(session);
        setUser(session.user);
        queryClient.clear();
      });
    },
    [queryClient, memoizedUser?.id, memoizedUser?.username],
  );

  const impersonatedSessionLogout = useCallback(
    (resetTokens) => {
      StorageUtils.setItem(ACCESS_TOKEN_COOKIE, resetTokens.accessToken);
      StorageUtils.removeItem(IMPERSONATED_SESSION_COOKIE);
      StorageUtils.removeItem(USER_COOKIE);
      unstable_batchedUpdates(() => {
        setImpersonatedSessionContext(null);
        // go back to admin
        setUser(impersonatedSessionContext.admin);
        selectUserProfile("ADMINISTRATOR");
        queryClient.clear();
      });
    },
    [impersonatedSessionContext, queryClient, selectUserProfile],
  );

  const logout = useCallback(() => {
    SignolUtils.removeSessionCookies();
    unstable_batchedUpdates(() => {
      setIsAuthenticated(false);
      setUser(null);
      queryClient.clear();
    });
    StorageUtils.setItem(LOGGING_OUT, Date.now().toString());
  }, [queryClient]);

  useEffect(() => {
    const cachedUser = StorageUtils.getObjectItem(USER_COOKIE);
    if (memoizedUser?.id && memoizedUser.id != cachedUser?.id) {
      StorageUtils.setObjectItem(USER_COOKIE, memoizedUser);
      StorageUtils.setItem(ROLE_COOKIE, activeRole);
      hotjarIdentifyUser();
    }
  }, [activeRole, memoizedUser]);

  const memoedValue = useMemo(
    () => ({
      user: memoizedUser,
      isAuthenticated,
      activeRole,
      activeRoleDisplayName,
      activeRoleRoutePrefix,
      avatarInitials,
      setAvatarInitials,
      impersonatedSessionContext,
      hasAdminRole,
      hasManagerRole,
      hasOperatorRole,
      isActiveRoleAdmin,
      isActiveRoleManager,
      isActiveRoleOperator,
      hasRole,
      hasRoleSwitcher,
      selectUserProfile,
      setUserTokens,
      setImpersonatedSession,
      impersonatedSessionLogout,
      logout,
    }),
    [
      memoizedUser,
      isAuthenticated,
      activeRole,
      activeRoleDisplayName,
      activeRoleRoutePrefix,
      avatarInitials,
      setAvatarInitials,
      impersonatedSessionContext,
      hasAdminRole,
      hasManagerRole,
      hasOperatorRole,
      isActiveRoleAdmin,
      isActiveRoleManager,
      isActiveRoleOperator,
      hasRole,
      hasRoleSwitcher,
      selectUserProfile,
      setUserTokens,
      setImpersonatedSession,
      impersonatedSessionLogout,
      logout,
    ],
  );
  return (
    <AuthContext.Provider value={memoedValue}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  currentUser: PropTypes.object,
};

export { useAuthContext };

export default AuthProvider;
